import { sendAnalyticsEvent } from '@faststore/sdk'
import { graphql, useStaticQuery } from 'gatsby'
import { useBreakpoint } from 'gatsby-plugin-breakpoints'
import { useEffect, useRef, useState } from 'react'
import { PauseIcon } from 'src/components/icons/PauseIcon'
import { PlayIcon } from 'src/components/icons/PlayIcon'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import { A11y, Autoplay, Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react'
import type {
  HeroCarouselNewItem,
  HeroCarouselNewProps,
} from '../HeroCarousel/hero-carousel'
import { Content } from './Slide/Content/Content'
import { HeroImageWrapper } from './Slide/HeroImageWrapper'

const options = {
  threshold: 0.85,
}

type ObserverRef = {
  target: HTMLElement | null
  observer: IntersectionObserver | null
}

const HeroCarouselNew = (data: HeroCarouselNewProps) => {
  const [isAutoPlayActive, setIsAutoPlayActive] = useState(
    data?.autoplay?.isEnabled
  )
  const [isBodyMaxWidth, setIsBodyMaxWidth] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const swiperRef = useRef<SwiperRef | null>(null)
  const observerRef = useRef<ObserverRef[]>([])
  const { isMobile } = useBreakpoint()

  const heroCarouselImages = useStaticQuery(
    graphql`
      query HeroCarouselImagesNew {
        allFile {
          nodes {
            publicURL
            childImageSharp {
              gatsbyImageData(
                formats: [WEBP]
                placeholder: BLURRED
                width: 1090
              )
            }
          }
        }
      }
    `
  )

  const isAutoPlayEnabled = data?.autoplay?.isEnabled
  const autoPlayDelay = data?.autoplay?.duration

  const toggleAutoplay = (currentState: boolean | undefined) => {
    if (!currentState) {
      setIsAutoPlayActive(true)
      swiperRef.current?.swiper.autoplay.start()
    } else {
      setIsAutoPlayActive(false)
      swiperRef.current?.swiper.autoplay.stop()
    }
  }

  const clickHandler = (item: HeroCarouselNewItem, index: number) => {
    const params = item.analytics
    sendAnalyticsEvent({
      // @ts-expect-error
      name: 'promo_click',
      params: {
        ...params,
        // @ts-expect-error
        promotion_position: index + 1,
      },
    })
  }

  const intersectionCallback = (
    entries: IntersectionObserverEntry[],
    observer: IntersectionObserver
  ) => {
    const [entry] = entries
    if (entry.isIntersecting) {
      const itemPosition = Number(
        (entry.target as HTMLElement).dataset.position
      )
      const item = data?.carouselItems?.[itemPosition - 1]
      const params = item.analytics
      sendAnalyticsEvent({
        // @ts-expect-error
        name: 'promo_impressions',
        params: {
          ...params,
          // @ts-expect-error
          promotion_position: itemPosition,
        },
      })

      observer.disconnect()
    }
  }

  useEffect(() => {
    const isBodyMaxWidth = window.innerWidth >= 1520
    setIsBodyMaxWidth(isBodyMaxWidth)
  }, [])

  useEffect(() => {
    if (observerRef.current.length) {
      observerRef.current.forEach((el, index: number) => {
        if (el.target) {
          const observer = new IntersectionObserver(
            intersectionCallback,
            options
          )
          observerRef.current[index].observer = observer
          observer.observe(el.target)
        }
      })
    }

    return () => {
      observerRef.current.forEach((el) => {
        if (el.observer) {
          el.observer.disconnect()
        }
      })
    }
  }, [observerRef.current])

  if (!data?.carouselItems?.length) {
    return <></>
  }

  return (
    <div
      id="hero-carousel"
      ref={containerRef}
      className="mt-4 ml-4 body-max-width:-mx-[calc((100vw-1520px)/2-32px)] relative"
    >
      <button
        className={`flex absolute top-2 left-2 bg-transparent items-center justify-center p-0 z-[2] cursor-pointer`}
        onClick={() => toggleAutoplay(isAutoPlayActive)}
        aria-label={isAutoPlayActive ? 'Pause autoplay' : 'Start autoplay'}
        aria-pressed={isAutoPlayActive}
      >
        {isAutoPlayEnabled &&
          (isAutoPlayActive ? (
            <PauseIcon
              width={isMobile ? 18 : 20}
              height={isMobile ? 18 : 20}
              color="#000"
              ariaHidden
              focusable={false}
            />
          ) : (
            <PlayIcon
              width={isMobile ? 18 : 20}
              height={isMobile ? 18 : 20}
              color="#000"
              ariaHidden
              focusable={false}
            />
          ))}
      </button>
      <Swiper
        ref={swiperRef}
        modules={[Navigation, Autoplay, Pagination, A11y]}
        slidesPerView={'auto'}
        navigation={{
          prevEl: '.hero-swiper-button-prev',
          nextEl: '.hero-swiper-button-next',
        }}
        grabCursor={true}
        slidesOffsetAfter={isBodyMaxWidth ? 0 : 16}
        spaceBetween={8}
        pagination={{ clickable: true }}
        {...(isAutoPlayEnabled
          ? {
              autoplay: {
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
                delay: autoPlayDelay,
              },
            }
          : {})}
        a11y={{
          enabled: true,
          containerMessage: 'Hero carousel',
          containerRole: 'region',
          containerRoleDescriptionMessage: 'carousel',
          itemRoleDescriptionMessage: 'slide',
          prevSlideMessage: 'Previous slide',
          nextSlideMessage: 'Next slide',
        }}
      >
        {data.carouselItems.map((item: HeroCarouselNewItem, index: number) => (
          <SwiperSlide
            key={`slide-${index}`}
            className="!w-[315px] !h-[400px] md:!w-[545px] md:!h-[590px]"
          >
            <a
              className="flex flex-col h-full no-underline"
              ref={(el) => {
                observerRef.current[index] = { target: null, observer: null }
                observerRef.current[index].target = el
              }}
              onClick={() => clickHandler(item, index)}
              data-position={index + 1}
              href={item?.content?.button?.to}
              key={`slide-${item.content.title.text}-${index}`}
            >
              <HeroImageWrapper
                src={item?.image?.src}
                alt={item?.image?.alt}
                heroImages={heroCarouselImages.allFile}
                index={index}
              />
              <Content item={item} />
            </a>
          </SwiperSlide>
        ))}
        <button className="!lg:ml-4 !-translate-y-[50%] swiper-button-prev hero-swiper-button-prev" />
        <button className="!lg:mr-4 !-translate-y-[50%] swiper-button-next hero-swiper-button-next" />
      </Swiper>
    </div>
  )
}

export default HeroCarouselNew
